/**
 * <ax-shipping-countdown>
 * Wraps <countdown-timer> (theme.js:5119, lit expires-at au mount uniquement) et le
 * re-clone à chaque refresh avec la cible calculée selon le jour/heure Europe/Paris.
 * Jours d'expédition : Lun-Jeu uniquement.
 */
class AxShippingCountdown extends HTMLElement {
  connectedCallback() {
    this.cutoffHour = this._parseCutoff(this.dataset.cutoffHour);
    this.timezone = this._resolveTimezone(this.dataset.timezone);
    this.excludedDates = this._parseExcludedDates(this.dataset.excludedDates || '');
    this.messages = {
      today: this.dataset.messageToday || '',
      tomorrow: this.dataset.messageTomorrow || '',
      monday: this.dataset.messageMonday || '',
      nextBusinessDay:
        this.dataset.messageNextBusinessDay || this.dataset.messageMonday || '',
    };

    const initialTimer = this.querySelector('countdown-timer');
    if (!initialTimer) return;
    this._timerTemplate = initialTimer.outerHTML;

    const scope = this.closest('.shopify-section, [data-block-id]') || this;
    this.messageEl = scope.querySelector('[data-shipping-message]');

    this._onVisibilityChange = () => {
      if (document.visibilityState === 'visible') this._refresh();
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    this._refresh();
  }

  disconnectedCallback() {
    this._clearRefreshTimeout();
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
  }

  _refresh() {
    const state = this._computeTarget();
    this.render(state);
    this._scheduleNextRefresh(state.targetDate);
  }

  render({ mode, targetDate } = this._computeTarget()) {
    const message = this.messages[mode] || this.messages.monday;

    if (this.messageEl && message) {
      this.messageEl.textContent = message;
    }

    const tmp = document.createElement('div');
    tmp.innerHTML = this._timerTemplate;
    const fresh = tmp.firstElementChild;
    fresh.setAttribute('expires-at', targetDate.toISOString());
    const lang = document.documentElement.lang || 'fr';
    const dateLabel = targetDate.toLocaleString(lang, {
      timeZone: this.timezone,
      dateStyle: 'full',
      timeStyle: 'short',
    });
    fresh.setAttribute('aria-label', `${message} — ${dateLabel}`);

    const current = this.querySelector('countdown-timer');
    if (current) current.replaceWith(fresh);
    else this.appendChild(fresh);
  }

  _computeTarget() {
    const parts = this._getZonedParts(new Date(), this.timezone);
    const day = parts.weekday;
    const hour = parts.hour;
    const cutoff = this.cutoffHour;

    let daysAhead;
    let initialMode;
    if (day >= 1 && day <= 4 && hour < cutoff) {
      daysAhead = 0;
      initialMode = 'today';
    } else if (day >= 1 && day <= 3) {
      daysAhead = 1;
      initialMode = 'tomorrow';
    } else {
      // Jeu après cutoff (+4), Ven (+3), Sam (+2), Dim (+1) → lundi
      daysAhead = { 4: 4, 5: 3, 6: 2, 0: 1 }[day];
      initialMode = 'monday';
    }

    let candidate = this._addDays(parts.year, parts.month, parts.dayOfMonth, daysAhead);
    let safety = 0;
    while (this._isNonShippingDay(candidate) && safety < 60) {
      daysAhead += 1;
      candidate = this._addDays(parts.year, parts.month, parts.dayOfMonth, daysAhead);
      safety += 1;
    }

    const candidateWd = new Date(
      Date.UTC(candidate.year, candidate.month - 1, candidate.day)
    ).getUTCDay();

    let mode;
    if (daysAhead === 0) {
      mode = 'today';
    } else if (daysAhead === 1 && initialMode !== 'monday') {
      mode = 'tomorrow';
    } else if (candidateWd === 1) {
      mode = 'monday';
    } else {
      mode = 'nextBusinessDay';
    }

    const targetDate = this._buildZonedCutoff(
      candidate.year,
      candidate.month,
      candidate.day,
      cutoff,
      this.timezone
    );
    return { mode, targetDate };
  }

  _parseCutoff(raw) {
    const n = parseInt(raw, 10);
    return n >= 0 && n <= 23 ? n : 15;
  }

  _resolveTimezone(raw) {
    const tz = raw || 'Europe/Paris';
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: tz });
      return tz;
    } catch {
      return 'Europe/Paris';
    }
  }

  _parseExcludedDates(raw) {
    if (!raw) return new Set();
    return new Set(
      raw
        .split(/[,\s;]+/)
        .map((s) => s.trim())
        .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s))
    );
  }

  _isNonShippingDay({ year, month, day }) {
    const d = new Date(Date.UTC(year, month - 1, day));
    const wd = d.getUTCDay();
    if (wd === 0 || wd === 5 || wd === 6) return true;
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return this.excludedDates.has(iso);
  }

  _addDays(year, month, day, daysAhead) {
    const d = new Date(Date.UTC(year, month - 1, day + daysAhead));
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
    };
  }

  _getZonedParts(date, tz) {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hour12: false,
    });
    const map = {};
    fmt.formatToParts(date).forEach((p) => {
      map[p.type] = p.value;
    });
    const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const hourRaw = map.hour === '24' ? '0' : map.hour;
    return {
      year: parseInt(map.year, 10),
      month: parseInt(map.month, 10),
      dayOfMonth: parseInt(map.day, 10),
      hour: parseInt(hourRaw, 10),
      minute: parseInt(map.minute, 10),
      weekday: weekdayMap[map.weekday],
    };
  }

  _buildZonedCutoff(year, month, day, hour, tz) {
    const normalized = new Date(Date.UTC(year, month - 1, day));
    const ny = normalized.getUTCFullYear();
    const nm = normalized.getUTCMonth() + 1;
    const nd = normalized.getUTCDate();

    const utcGuess = Date.UTC(ny, nm - 1, nd, hour, 0, 0);
    for (let offsetQuarter = -56; offsetQuarter <= 48; offsetQuarter++) {
      const offsetMs = offsetQuarter * 15 * 60 * 1000;
      const candidate = new Date(utcGuess - offsetMs);
      const candParts = this._getZonedParts(candidate, tz);
      if (
        candParts.hour === hour &&
        candParts.minute === 0 &&
        candParts.dayOfMonth === nd &&
        candParts.month === nm &&
        candParts.year === ny
      ) {
        return candidate;
      }
    }
    return new Date(utcGuess - 3600 * 1000);
  }

  _scheduleNextRefresh(targetDate) {
    this._clearRefreshTimeout();
    const now = Date.now();
    const parts = this._getZonedParts(new Date(), this.timezone);
    const nextMidnight = this._buildZonedCutoff(
      parts.year,
      parts.month,
      parts.dayOfMonth + 1,
      0,
      this.timezone
    );
    const nextEvent = Math.min(targetDate.getTime(), nextMidnight.getTime());
    const wait = nextEvent - now + 1500;
    if (wait <= 0) return;
    this._refreshTimeout = setTimeout(() => this._refresh(), wait);
  }

  _clearRefreshTimeout() {
    if (this._refreshTimeout) {
      clearTimeout(this._refreshTimeout);
      this._refreshTimeout = null;
    }
  }
}

if (!customElements.get('ax-shipping-countdown')) {
  customElements.define('ax-shipping-countdown', AxShippingCountdown);
}
